import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { endpoints } from 'src/utils/axios';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';
import useFirebaseApp from 'src/hooks/use-firebase-app';
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';

import { homeActions } from 'src/store/store';
import { useDispatch, useSelector } from 'react-redux';

import { encrypt, decrypt } from 'src/utils/custom-function';

import OneSignal from 'react-onesignal';

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';
const STORAGE_USER_KEY = 'user';

const GIVE_ALL_ACCESS = false;

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storeDispatch = useDispatch();
  const { firebaseAuth } = useFirebaseApp();

  const userData = useSelector((state) => state.home.user);
  const myPermissionData = useSelector((state) => state.home.myPermissions);

  // LOGOUT
  const logout = useCallback(async () => {
    console.log("Need logout");
    
    signOut(firebaseAuth);

    setSession(null);

    if(OneSignal.User.PushSubscription.optedIn){
      OneSignal.logout();
    }

    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  const initialize = useCallback(async () => {
    try {
      const accessToken = sessionStorage.getItem(STORAGE_KEY);
      const userCipherText = sessionStorage.getItem(STORAGE_USER_KEY);

      console.log(accessToken);
      console.log(userCipherText);

      if (accessToken && isValidToken(accessToken) && userCipherText) {
        setSession(accessToken);

        dispatch({
          type: 'INITIAL',
          payload: {
            user: userCipherText,
          },
        });

        const userData = decrypt(userCipherText);
        console.log(userData);

        storeDispatch(
          homeActions.setUser(JSON.parse(userData))
        )
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      logout();
    }
  }, [logout]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // ACCESS TOKEN
  const requestAccessToken = useCallback(async () => {
    console.log("Access token requested");
    const response = sessionStorage.getItem(STORAGE_KEY);
    console.log("Access token received: " + response);

    return response;
  }, []);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const firebaseResponse = await signInWithEmailAndPassword(firebaseAuth, email, password);
    console.log({ firebaseResponse });

    const accessToken = await firebaseAuth.currentUser?.getIdToken();

    return accessToken;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    console.log("Forgot password requested for " + email);
    const response = await sendPasswordResetEmail(firebaseAuth, email);
    return response;
  }, []);

  const verifyOldPassword = useCallback(async (currentPassword) => {
    const credential = EmailAuthProvider.credential(firebaseAuth.currentUser.email, currentPassword);

    try {
      await reauthenticateWithCredential(firebaseAuth.currentUser, credential);
      return null;
    } catch (error) {
      console.error("Error during re-authentication or password update:", error.message);
      console.log(error.code);
      return error.code;
    }
  }, []);

  const requestUpdatePassword = useCallback(async (newPassword) => {
    console.log("Update password requested");
    const response = await updatePassword(firebaseAuth.currentUser, newPassword);
    return response;
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const data = {
      email,
      password,
      firstName,
      lastName,
    };

    const response = await axios.post(endpoints.auth.register, data);

    const { accessToken, user } = response.data;

    sessionStorage.setItem(STORAGE_KEY, accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);

  // LOGIN RESPONSE
  const saveAccessToken = useCallback((accessToken) => {
    console.log(accessToken);
    setSession(accessToken);
  }, []);

  const saveLoginResponse = useCallback((userObj) => {
    console.log("User data...");
    console.log(userObj);

    let user;

    // email: "staff+010@gmail.com"
    // firstName: "staff"  
    // isDeactivated: false
    // isSuperAdmin: false
    // lastName: "010"
    // nic: "111111111111"
    // phone: "+94761111110"
    // profilePicture: "https://renteaze-dev.s3.ap-southeast-1.amazonaws.com/profile/4bb85b82-4d31-4827-93a9-93a3b5e80255?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Credential=AKIATVB3MKOPBZVGSSOJ%2F20251204%2Fap-southeast-1%2Fs3%2Faws4_request&X-Amz-Date=20251204T104536Z&X-Amz-Expires=3600&X-Amz-Signature=3d4ec346c3bd4fa414a3cd39b9777bc2f78e3e9d475ca542f767426010d9c537&X-Amz-SignedHeaders=host&x-amz-checksum-mode=ENABLED&x-id=GetObject"
    // staffRole: {
    //   id: "692fb36493e7c505db0f952f"
    //   name: "Sales"
    // }

    if(userObj){
      user = JSON.stringify(userObj);
      console.log("User...");
      console.log(user);

      const encryptedData = encrypt(user);
      sessionStorage.setItem(STORAGE_USER_KEY, encryptedData);
    }
    
  const encryptedData = encrypt(user);
  sessionStorage.setItem(STORAGE_USER_KEY, encryptedData);

  dispatch({
    type: 'LOGIN',
    payload: {
      user: encryptedData,
    },
  });

    storeDispatch(
      homeActions.setUser(userObj)
    )
  }, []);

  // CHECK ACCESS
  const hasAccess = useCallback((sectionName, actionName) => {
    console.log("Need role permission check...");

    console.log(userData);
    console.log(myPermissionData);

    console.log(sectionName);
    console.log(actionName);
    
    if(GIVE_ALL_ACCESS){
      return true;
    }

    if(userData.isSuperAdmin){
      return true;
    }

    let hasAccess = false;

    for(let i = 0; i < myPermissionData?.length; i++){
      if(myPermissionData[i].name === sectionName){
        const actionList = JSON.parse(myPermissionData[i].action);
        hasAccess = actionList.includes(actionName);

        break;
      }
    }

    return hasAccess;
  }, [userData, myPermissionData]);
  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      requestAccessToken,
      login,
      register,
      forgotPassword,
      requestUpdatePassword,
      verifyOldPassword,
      logout,
      saveLoginResponse,
      saveAccessToken,
      hasAccess
    }),
    [
      requestAccessToken, 
      login, 
      logout, 
      register, 
      forgotPassword, 
      requestUpdatePassword, 
      verifyOldPassword, 
      saveLoginResponse, 
      saveAccessToken,
      state.user, 
      status
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};